import React, { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { SEO } from "../components/SEO";
import { ChevronLeft, Calendar, User, Clock, Share2, Facebook, Twitter, Linkedin, Play } from "lucide-react";

export const BlogPost = () => {
  const { slug } = useParams();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  return (
    <div className="bg-white min-h-screen pb-20">
      <SEO 
        title="O Guia Definitivo: Como um ERP revoluciona o chão de fábrica | RosTech" 
        description="Descubra o impacto de um sistema ERP na gestão da sua oficina mecânica e como ele pode revolucionar seu chão de fábrica."
        canonicalUrl={`/blog/${slug}`}
      />

      {/* Article Header */}
      <header className="pt-20 pb-12 bg-slate-50 border-b border-slate-100">
        <div className="max-w-4xl mx-auto px-6">
          <Link to="/blog" className="inline-flex items-center text-blue-600 font-bold text-sm tracking-wide uppercase hover:text-blue-800 transition-colors mb-8">
            <ChevronLeft className="w-4 h-4 mr-1" /> Voltar para o Blog
          </Link>
          
          <div className="flex items-center gap-3 mb-6">
            <span className="bg-blue-600 text-white text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded">
              Software ERP
            </span>
            <span className="text-slate-500 text-sm font-medium flex items-center">
              <Clock className="w-4 h-4 mr-1.5" /> 8 min de leitura
            </span>
          </div>

          <h1 className="text-4xl md:text-5xl font-extrabold text-slate-900 leading-tight mb-6">
            O Guia Definitivo: Como um ERP revoluciona o chão de fábrica
          </h1>

          <div className="flex items-center justify-between border-t border-slate-200 mt-8 pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-bold text-lg">
                R
              </div>
              <div>
                <p className="text-slate-900 font-bold text-sm">Equipe RosTech</p>
                <p className="text-slate-500 text-xs flex items-center mt-0.5">
                  <Calendar className="w-3 h-3 mr-1" /> 19 Abr, 2026
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <span className="text-slate-500 text-sm font-medium mr-2 hidden sm:inline-block">Compartilhar:</span>
              <button className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-600 hover:bg-blue-100 hover:text-blue-600 transition-colors">
                <Facebook className="w-4 h-4" />
              </button>
              <button className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-600 hover:bg-blue-100 hover:text-blue-600 transition-colors">
                <Twitter className="w-4 h-4" />
              </button>
              <button className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-600 hover:bg-blue-100 hover:text-blue-600 transition-colors">
                <Linkedin className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        {/* Cover Video/Image */}
        <div className="relative rounded-2xl overflow-hidden mb-12 shadow-xl border border-slate-100 aspect-video bg-slate-900 flex items-center justify-center group">
           <img 
              src="https://images.unsplash.com/photo-1621293954908-907159247fc8?auto=format&fit=crop&q=80&w=1200" 
              alt="Mecânico com tablet" 
              className="absolute inset-0 w-full h-full object-cover opacity-80 group-hover:scale-105 transition-transform duration-700" 
           />
           <div className="absolute inset-0 bg-black/40 flex items-center justify-center pointer-events-none">
              <div className="w-20 h-20 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center border-2 border-white/50">
                 <Play className="w-8 h-8 text-white ml-2" />
              </div>
           </div>
           {/* In a real scenario, this could be a playable iframe like YouTube on click */}
        </div>

        <article className="prose prose-lg prose-slate max-w-none prose-headings:font-bold prose-headings:text-slate-900 prose-a:text-blue-600 prose-img:rounded-2xl">
          <p className="lead text-xl text-slate-600 font-medium mb-8">
            Administrar uma oficina mecânica ou loja de autopeças já foi sinônimo de pranchetas, cadernos manchados de graxa e uma infinidade de planilhas. Hoje, a tecnologia e a exigência dos clientes transformaram esse cenário. O ERP (Enterprise Resource Planning) não é mais um luxo, mas uma necessidade de sobrevivência.
          </p>

          <h2>O fim do achismo na oficina</h2>
          <p>
            Lembra daquela época em que você perguntava ao seu mecânico parceiro quando o carro do cliente João estaria pronto, e a resposta era um "acho que até o final da tarde"? Com um sistema integrado operando no meio do chão de fábrica, esse tipo de resposta desaparece.
          </p>
          <p>
            O fluxo de entrada e saída de veículos, a alocação de técnicos e a esteira de serviços tornam-se visuais e mensuráveis.
          </p>

          <img 
            src="https://images.unsplash.com/photo-1581092580497-e0d23cbdf1dc?auto=format&fit=crop&q=80&w=1000" 
            alt="Dashboard de produtividade no chão de fábrica" 
          />
          <figcaption className="text-center text-sm text-slate-500 mt-2 mb-8">Aplicativo móvel no chão de fábrica garantindo controle total.</figcaption>

          <h2>Controle de Peças: O Gargalo Financeiro</h2>
          <p>
            Em oficinas, não controlar peças é igual a rasgar dinheiro. O ERP vincula diretamente a Ordem de Serviço (O.S.) ao inventário.
          </p>
          <ul>
            <li><strong>Baixa automática:</strong> Quando uma peça é adicionada à OS, o sistema já reserva no estoque.</li>
            <li><strong>Alerta de reposição:</strong> Avisos de estoque mínimo impedem que você fique sem giro.</li>
            <li><strong>Múltiplos fornecedores:</strong> Comparação rápida para aumentar sua margem de lucro na venda de peças.</li>
          </ul>

          <div className="bg-blue-50 border-l-4 border-blue-600 p-6 my-8 rounded-r-xl">
            <h4 className="text-blue-800 font-bold mt-0">Dica Prática</h4>
            <p className="text-blue-900 mb-0">Implemente um leitor de código de barras para o estoque. Ele acelera a baixa das peças e reduz a quase zero o erro de digitação dos mecânicos na hora de lançar os itens na O.S.</p>
          </div>

          <h2>Vídeo: Transformando OS em Faturamento</h2>
          <p>Assista ao nosso especialista demonstrando como o fluxo da OS digital impacta diretamente no seu caixa, reduzindo a inadimplência e melhorando a aprovação de orçamentos pelo cliente.</p>
          
          <div className="aspect-video w-full rounded-2xl overflow-hidden shadow-lg border border-slate-200 my-8">
             <iframe 
               width="100%" 
               height="100%" 
               src="https://www.youtube-nocookie.com/embed/zpOULjyy-n8?rel=0&controls=0&showinfo=0" 
               title="Demonstração do Fluxo de OS" 
               allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
               allowFullScreen
               className="border-0"
             ></iframe>
          </div>

          <h2>A experiência do cliente</h2>
          <p>
            Sua oficina não arruma apenas carros, ela atende pessoas. O cliente moderno não tem tempo a perder e exige transparência. Sistemas como a RosTech revolucionam esse atendimento através do <strong>Checklist Digital</strong>.
          </p>
          <p>
            Tirar fotos de peças desgastadas e enviar um link de aprovação com imagens diretamente para o WhatsApp do cliente elimina o "empurra-empurra" e aumenta enormemente a confiança no seu diagnóstico.
          </p>

          <h2>Conclusão</h2>
          <p>
            Implantar um ERP exige adaptação, mudança cultural e engajamento da equipe, desde a recepção até o lavador. Contudo, os ganhos em produtividade, segurança financeira e redução de retrabalho pagam o investimento nos primeiros meses.
          </p>
        </article>

        {/* Tags */}
        <div className="flex flex-wrap gap-2 mt-12 pt-8 border-t border-slate-100">
          <span className="bg-slate-100 text-slate-600 px-3 py-1 rounded-full text-sm font-medium hover:bg-slate-200 cursor-pointer transition-colors">ERP</span>
          <span className="bg-slate-100 text-slate-600 px-3 py-1 rounded-full text-sm font-medium hover:bg-slate-200 cursor-pointer transition-colors">Gestão de Oficina</span>
          <span className="bg-slate-100 text-slate-600 px-3 py-1 rounded-full text-sm font-medium hover:bg-slate-200 cursor-pointer transition-colors">Tecnologia Automotiva</span>
          <span className="bg-slate-100 text-slate-600 px-3 py-1 rounded-full text-sm font-medium hover:bg-slate-200 cursor-pointer transition-colors">Inovação</span>
        </div>
      </main>

      {/* Suggested Articles */}
      <section className="bg-slate-50 py-16 border-t border-slate-200">
        <div className="max-w-4xl mx-auto px-6">
          <h3 className="text-2xl font-extrabold text-slate-900 mb-8">Artigos Relacionados</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
            {/* Suggestion 1 */}
            <Link to="/blog/5-dicas-essenciais-estoque" className="group rounded-2xl bg-white overflow-hidden shadow-sm hover:shadow-xl transition-all border border-slate-100">
              <div className="h-48 overflow-hidden">
                 <img src="https://images.unsplash.com/photo-1530982011887-3cc11cc85693?auto=format&fit=crop&q=80&w=800" alt="Estoque autopeças" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              </div>
              <div className="p-6">
                <span className="text-emerald-500 text-[10px] font-bold uppercase tracking-wider">Gestão</span>
                <h4 className="text-lg font-bold text-slate-900 mt-2 mb-2 group-hover:text-blue-600 transition-colors line-clamp-2">
                  5 Dicas essenciais para organizar o estoque de autopeças e evitar perdas
                </h4>
                <p className="text-slate-500 flex items-center text-xs mt-4">
                  <Calendar className="w-3 h-3 mr-1" /> 18 Abr, 2026
                </p>
              </div>
            </Link>

            {/* Suggestion 2 */}
            <Link to="/blog/tecnologia-automotiva-oficinas" className="group rounded-2xl bg-white overflow-hidden shadow-sm hover:shadow-xl transition-all border border-slate-100">
              <div className="h-48 overflow-hidden">
                 <img src="https://images.unsplash.com/photo-1560958089-b8a1929cea89?auto=format&fit=crop&q=80&w=600" alt="Carro elétrico" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              </div>
              <div className="p-6">
                <span className="text-blue-500 text-[10px] font-bold uppercase tracking-wider">Inovação</span>
                <h4 className="text-lg font-bold text-slate-900 mt-2 mb-2 group-hover:text-blue-600 transition-colors line-clamp-2">
                  Tecnologia automotiva: o futuro das oficinas e carros elétricos
                </h4>
                <p className="text-slate-500 flex items-center text-xs mt-4">
                  <Calendar className="w-3 h-3 mr-1" /> 10 Abr, 2026
                </p>
              </div>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
